import produce from 'immer';
import * as _ from 'lodash';
import { ActionStatus } from 'thunkless';

import * as UUID from '@/lib/uuid';
import { EventElementType } from '@/constants/event-element-type.enum';

import type { AppAction } from '../actions';
import { InboxTypes } from '../actions/inbox';
import { EventTypes } from '../actions/event';
import { TicketTypes } from '../actions/ticket';

export interface TicketState {
  data: Record<string, any>;
  loadingStatus: Record<string, any>;
}

export const initialState: TicketState = {
  data: {},
  loadingStatus: {},
};

export const reducer = produce((state: TicketState, action: AppAction) => {
  switch (action.type) {
    case EventTypes.APPEND_EVENTS: {
      /* eslint-disable @typescript-eslint/naming-convention */
      action.payload.forEach(({ event_id, temporary_id, element, ticket, preview }) => {
        const { element_type } = element;

        // prepend ticket if it doesn't exist
        if (!state.data[ticket.uuid]) {
          const {
            id, uuid, unaddressed, status, origin, channel, contact,
            owner_hash_id, created_at, account_subdomain, last_activity_preview,
          } = ticket;
          state.data[ticket.uuid] = {
            id,
            uuid,
            contactId: contact?.id,
            unaddressed,
            status,
            origin,
            channel,
            user: { id: owner_hash_id },
            can_message: true,
            lastActivityId: event_id,
            lastActivityPreview: last_activity_preview,
            created_at,
            account_subdomain,
          };
        }

        // prepend the event id to the list
        const updateEvents = (events = []) => {
          // we already have this exact event_id in the list
          if (events.indexOf(event_id) >= 0) return events;

          const tempIndex = events.indexOf(temporary_id);
          if (tempIndex >= 0) {
            events[tempIndex] = event_id;
            return events;
          }
          return [event_id, ...events];
        };
        const eventsPath = element_type === EventElementType.NOTE && element.call_id
          ? ['data', ticket.uuid, 'callNotes', element.call_id]
          : ['data', ticket.uuid, 'events'];
        _.update(state, eventsPath, updateEvents);
        if (element_type === EventElementType.NOTE) {
          _.update(state, ['data', ticket.uuid, 'avo_notes'], updateEvents);
        }

        // change the ticket state
        switch (element_type) {
          case EventElementType.SMS:
          case EventElementType.MMS:
          case EventElementType.CALL:
            _.set(state, ['data', ticket.uuid, 'user', 'id'], ticket.owner_hash_id);
            _.set(state, ['data', ticket.uuid, 'lastActivityId'], event_id);
            _.set(state, ['data', ticket.uuid, 'lastActivityPreview'], preview);
            break;
          case EventElementType.UNADDRESSED_CHANGE:
            state.data[ticket.uuid].unaddressed = element.unaddressed;
            break;
          case EventElementType.STATUS_CHANGE:
            state.data[ticket.uuid].status = element.status;
            break;
          case EventElementType.OWNER_CHANGE:
            _.set(state.data, [ticket.uuid, 'user', 'id'], element.owner_id);
            break;
        }
      });

      break;
      /* eslint-enable @typescript-eslint/naming-convention */
    }

    case InboxTypes.FETCH_INBOX_TICKETS_SUCCESS: {
      const { data } = action.payload;
      data.forEach(({ last_activity, contact, ...ticket }) => {
        ticket.lastActivityId = last_activity?.id;
        ticket.lastActivityPreview = last_activity?.preview;
        ticket.contactId = contact?.id;
        const existingData = state.data[ticket.uuid];
        if (!existingData?.events && last_activity?.id) ticket.events = [last_activity?.id];

        if (existingData) Object.assign(existingData, ticket);
        else state.data[ticket.uuid] = ticket;
      });
      break;
    }

    case EventTypes.FETCH_EVENTS_SUCCESS: {
      const { data } = action.payload;
      const { after, ticketUuid } = action.meta;
      const [newEvents, newCallNotes] = data.reduce((acc, { id, element_type, element_data }) => {
        if (element_type !== EventElementType.NOTE || !element_data.call_id) {
          // Not a call note => add to the events array
          acc[0].push(id);
        } else {
          // Add to call notes
          let notesForCall = acc[1][element_data.call_id];
          if (!notesForCall) {
            notesForCall = [];
            acc[1][element_data.call_id] = notesForCall;
          }
          notesForCall.push(id);
        }
        return acc;
      }, [[], {}]);

      _.update(state, ['data', ticketUuid, 'events'], (events) => {
        if (!events || !after) return newEvents;
        if (newEvents.length < 1) return events;

        newEvents.forEach((newEvent) => {
          if (events.indexOf(newEvent) < 0) events.push(newEvent);
        });

        return events;
      });
      _.update(state, ['data', ticketUuid, 'callNotes'], (callNotes) => {
        if (!callNotes || !after) return newCallNotes;
        Object.entries(newCallNotes).forEach(([k, v]) => {
          callNotes[k] = callNotes[k] ? callNotes[k].concat(v) : v;
        });
        return callNotes;
      });
      break;
    }

    case EventTypes.SEND_MESSAGE_REQUEST: {
      const { ticketUuid, temporaryId } = action.meta;
      _.update(state, ['data', ticketUuid, 'events'], (events = []) => [temporaryId].concat(events));
      break;
    }

    case TicketTypes.FETCH_TICKET_REQUEST: {
      const { uuid } = action.meta;
      state.loadingStatus[uuid] = ActionStatus.BUSY;
      break;
    }
    case TicketTypes.FETCH_TICKET_SUCCESS: {
      const { data } = action.payload;
      const { tickets: [{ last_activity: lastActivity, contact, ...ticketData }] } = data;
      const { uuid } = action.meta;
      ticketData.lastActivityId = lastActivity?.id;
      ticketData.lastActivityPreview = lastActivity?.preview;
      ticketData.contactId = contact?.id;
      ticketData.canView = true;

      state.loadingStatus[uuid] = ActionStatus.SUCCESS;
      _.update(state, ['data', uuid], (ticket = {}) => Object.assign(ticket, ticketData));
      _.update(state, ['data', uuid, 'events'], (events = lastActivity?.id && [lastActivity.id]) => events);
      _.set(state, ['data', uuid, 'avo_notes'], ticketData.avo_notes.map(({ id }) => id).reverse());
      break;
    }
    case TicketTypes.FETCH_TICKET_FAILURE: {
      const { uuid } = action.meta;
      state.loadingStatus[uuid] = ActionStatus.FAILURE;
      break;
    }

    case TicketTypes.CREATE_NOTE_REQUEST: {
      const { ticket, temporaryId, callId } = action.meta;
      if (callId) {
        _.update(state, ['data', ticket, 'callNotes', callId], (notes = []) => [temporaryId].concat(notes));
      } else {
        _.update(state, ['data', ticket, 'events'], (events = []) => [temporaryId].concat(events));
      }
      _.update(state, ['data', ticket, 'avo_notes'], (notes = []) => [temporaryId].concat(notes));
      break;
    }

    case TicketTypes.SET_UNADDRESSED_REQUEST: {
      const { ticket, unaddressed } = action.meta;
      _.set(state, ['data', ticket, 'unaddressed'], unaddressed);
      _.set(state, ['data', ticket, 'setUnaddressedStatus'], ActionStatus.BUSY);
      break;
    }
    case TicketTypes.SET_UNADDRESSED_SUCCESS: {
      const { ticket } = action.meta;
      _.set(state, ['data', ticket, 'setUnaddressedStatus'], ActionStatus.SUCCESS);
      break;
    }
    case TicketTypes.SET_UNADDRESSED_FAILURE: {
      const { ticket, unaddressed } = action.meta;
      _.set(state, ['data', ticket, 'unaddressed'], !unaddressed);
      _.set(state, ['data', ticket, 'setUnaddressedStatus'], ActionStatus.FAILURE);
      break;
    }

    case TicketTypes.SET_STATUS_REQUEST: {
      const { ticket, newStatus } = action.meta;
      _.set(state, ['data', ticket, 'status'], newStatus);
      _.set(state, ['data', ticket, 'setStatusOfTicketStatus'], ActionStatus.BUSY);
      break;
    }
    case TicketTypes.SET_STATUS_SUCCESS: {
      const { ticket } = action.meta;
      _.set(state, ['data', ticket, 'setStatusOfTicketStatus'], ActionStatus.SUCCESS);
      break;
    }
    case TicketTypes.SET_STATUS_FAILURE: {
      const { ticket, currentStatus } = action.meta;
      _.set(state, ['data', ticket, 'status'], currentStatus);
      _.set(state, ['data', ticket, 'setStatusOfTicketStatus'], ActionStatus.FAILURE);
      break;
    }

    case TicketTypes.SET_OWNER_REQUEST: {
      const { ticket, newOwner } = action.meta;
      _.set(state, ['data', ticket, 'user', 'id'], newOwner);
      _.set(state, ['data', ticket, 'user', 'hash_id'], newOwner);
      _.set(state, ['data', ticket, 'setOwnerOfTicketStatus'], ActionStatus.BUSY);
      break;
    }

    case TicketTypes.SET_OWNER_SUCCESS: {
      const { ticket } = action.meta;
      _.set(state, ['data', ticket, 'setOwnerOfTicketStatus'], ActionStatus.SUCCESS);
      break;
    }

    case TicketTypes.SET_OWNER_FAILURE: {
      const { ticket, currentOwner } = action.meta;
      _.set(state, ['data', ticket, 'user', 'id'], currentOwner);
      _.set(state, ['data', ticket, 'user', 'hash_id'], currentOwner);
      _.set(state, ['data', ticket, 'setOwnerOfTicketStatus'], ActionStatus.FAILURE);
      break;
    }

    case TicketTypes.FETCH_PREVIOUS_TICKETS_REQUEST: {
      const { ticket } = action.meta;
      _.set(state, ['data', ticket, 'fetchPreviousTicketsStatus'], ActionStatus.BUSY);
      break;
    }
    case TicketTypes.FETCH_PREVIOUS_TICKETS_SUCCESS: {
      const { data: { tickets } } = action.payload;
      const { ticket } = action.meta;
      const { contact } = state.data[ticket] || {};
      _.set(state.data, [ticket, 'previousTickets'], tickets.map(({ uuid }) => uuid));
      _.merge(state.data, _.keyBy(tickets.map((t) => ({ ...t, contact })), 'uuid'));
      _.set(state.data, [ticket, 'fetchPreviousTicketsStatus'], ActionStatus.SUCCESS);
      _.set(state.data, [ticket, 'previousTicketsRenderId'], UUID.generate());
      break;
    }
    case TicketTypes.FETCH_PREVIOUS_TICKETS_FAILURE: {
      const { ticket } = action.meta;
      _.set(state, ['data', ticket, 'fetchPreviousTicketsStatus'], ActionStatus.FAILURE);
      break;
    }

    case TicketTypes.TOGGLE_PREVIOUS_TICKETS: {
      const { ticket } = action.meta;
      _.update(state.data, [ticket, 'showPreviousTickets'], (v) => !v);
      _.set(state.data, [ticket, 'previousTicketsRenderId'], UUID.generate());
    }
  }
}, initialState);
